import React, { useMemo, useRef, useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import axios from '@/utils/axios';
import { toast } from 'sonner';
import { TaskCard } from '@/components/TaskCard';
import { TaskListItem } from '@/components/TaskListItem';
import { getIconComponent } from "@/lib/statusIconsMap";
import { useFilteredTasks, useSortedTasks } from '@/hooks/useFilteredTasks';
import { useAuth } from "@/context/AuthContext";
import { QuickAddTask } from '@/components/QuickAddTask';
import { Skeleton } from "@/components/ui/skeleton";
import { STATUS_COLORS } from "@/lib/colorsMap";

// Need to be defined for tailwind to actually generate the classes
const GRID_COLUMN_TAILWIND_MAP = {
    1: 'md:grid-cols-1',
    2: 'md:grid-cols-2',
    3: 'md:grid-cols-3',
    4: 'md:grid-cols-4',
    5: 'md:grid-cols-5',
    6: 'md:grid-cols-6',
    7: 'md:grid-cols-7',
    8: 'md:grid-cols-8',
    9: 'md:grid-cols-9',
    10: 'md:grid-cols-10',
    11: 'md:grid-cols-11',
    12: 'md:grid-cols-12'
};

export function ProjectBoard({ isLoading, project, setProject, groupByAssignee = false, showBacklogColumn = false, filterByAssignee = new Set(), filterBy, sortBy, user }) {
    const ColumnLoadingSkeleton = () => (
        <Card className="flex h-full flex-col bg-muted/70 border-muted-foreground/20 dark:bg-muted/20">
            <CardHeader className="pt-7 pb-4">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Skeleton className="h-4 w-4 rounded-full" />
                    <Skeleton className="h-4 w-24" />
                </CardTitle>
            </CardHeader>
            <CardContent className="flex-1 p-3">
                <div className="space-y-4">
                    {[...Array(4)].map((_, i) => (
                        <Skeleton key={i} className="h-[80px] w-full" />
                    ))}
                </div>
            </CardContent>
        </Card>
    );

    if (isLoading || !project) {
        return (
            <div className={`grid gap-4 h-full grid-cols-1 ${GRID_COLUMN_TAILWIND_MAP[4]}`}>
                {[...Array(4)].map((_, i) => (
                    <div key={i} className="h-full">
                        <ColumnLoadingSkeleton />
                    </div>
                ))}
            </div>
        );
    }


    let filteredTasks = useFilteredTasks(project, {
        filterByAssignee,
        filterBy,
        user
    });
    filteredTasks = useSortedTasks(filteredTasks, sortBy);

    // Use project statuses
    const statusConfig = (project?.statuses || []).reduce((acc, status) => ({
        ...acc,
        [status.id]: {
            icon: getIconComponent(status.icon),
            color: STATUS_COLORS[status.color]?.text || `text-${status.color}`
        }
    }), {});

    const { axiosInstance } = useAuth();

    const getVisibleColumnCount = () => {
        return project.statuses
            .filter(status => showBacklogColumn || status.id !== 'backlog')
            .length;
    };

    const columns = useMemo(() => {
        if (!groupByAssignee) {
            return project.statuses
                .filter(status => showBacklogColumn || status.id !== 'backlog')
                .map(status => ({
                    id: status.id,
                    title: status.name,
                    tasks: filteredTasks.filter(task =>
                        (typeof task.status === 'object' ? task.status.id : task.status) === status.id
                    ),
                }));
        } else {
            const assignees = [...new Set(
                filteredTasks.map(task => (task.assignee ? task.assignee.id : 'unassigned'))
            )].sort((a, b) => {
                // Always put unassigned at the end
                if (a === 'unassigned') return 1;
                if (b === 'unassigned') return -1;

                // Get assignee info for comparison
                const aInfo = filteredTasks.find(task => task.assignee?.id === a)?.assignee;
                const bInfo = filteredTasks.find(task => task.assignee?.id === b)?.assignee;

                return aInfo?.name.localeCompare(bInfo?.name) || 0;
            });
          return assignees.flatMap(assigneeId => {
            const assigneeTasks = filteredTasks.filter(
              task => (task.assignee ? task.assignee.id : 'unassigned') === assigneeId
            );
            const assigneeInfo =
              assigneeId === 'unassigned'
                ? { id: 'unassigned', name: 'Unassigned', initials: '', avatarColor: 'bg-neutral-600' }
                : assigneeTasks[0]?.assignee || { id: assigneeId, name: 'Unknown', initials: '', avatarColor: 'bg-neutral-600' };

            return project.statuses
                .filter(status => showBacklogColumn || status.id !== 'backlog')
                .map(status => ({
                    id: `${assigneeId}-${status.id}`,
                    originalId: status.id,
                    title: status.name,
                    assigneeId: assigneeInfo.id,
                    assigneeName: assigneeInfo.name,
                    assigneeInitials: assigneeInfo.initials,
                    assigneeAvatarColor: assigneeInfo.avatarColor,
                    tasks: assigneeTasks.filter(task =>
                        (typeof task.status === 'object' ? task.status.id : task.status) === status.id
                    ),
                }));
          });
        }
      }, [project, groupByAssignee, showBacklogColumn, filteredTasks]);


    const handleTaskUpdate = (updatedTask) => {
        setProject((prevProject) => ({
            ...prevProject,
            tasks: prevProject.tasks.map((task) =>
                task.id === updatedTask.id
                    ? {
                        ...task,
                        ...updatedTask,
                        status: typeof updatedTask.status === 'object' 
                            ? updatedTask.status.id 
                            : updatedTask.status,
                        assignee: updatedTask.assignee || null,
                        priority: updatedTask.priority,
                    }
                    : task
            ),
        }));
    };

    const onDragEnd = async (result) => {
        if (!result.destination) return;

        const { source, destination, draggableId } = result;

        if (source.droppableId === destination.droppableId &&
            source.index === destination.index) {
            return;
        }

        const newColumns = [...columns];
        const sourceColumn = newColumns.find(col => col.id === source.droppableId);
        const destColumn = newColumns.find(col => col.id === destination.droppableId);
        const [movedTask] = sourceColumn.tasks.splice(source.index, 1);

        // When grouped by assignee, update both status and assignee
        const newStatus = groupByAssignee ? destColumn.originalId : destColumn.id;
        const oldStatus = groupByAssignee ? sourceColumn.originalId : sourceColumn.id;

        // Update task status
        movedTask.status = {
            id: newStatus,
            name: project.statuses.find(status => status.id === newStatus)?.name
        };

        // Update assignee when groupByAssignee is true
        if (groupByAssignee) {
            movedTask.assignee = destColumn.assigneeId === 'unassigned'
                ? null
                : {
                    id: destColumn.assigneeId,
                    name: destColumn.assigneeName,
                    initials: destColumn.assigneeInitials,
                    avatarColor: destColumn.assigneeAvatarColor
                };
        }

        destColumn.tasks.splice(destination.index, 0, movedTask);

        // Update positions for all tasks in affected columns
        destColumn.tasks.forEach((task, index) => {
            task.position = index;
        });

        if (source.droppableId !== destination.droppableId) {
            sourceColumn.tasks.forEach((task, index) => {
                task.position = index;
            });
        }

        setProject(prevProject => ({
            ...prevProject,
            tasks: prevProject.tasks.map(task =>
                task.id === movedTask.id ? {
                    ...task,
                    ...movedTask,
                    status: movedTask.status,
                    position: destination.index,
                    assignee: movedTask.assignee
                } : task
            )
        }));

        try {
            const patchTask = await axiosInstance.patch(`/tasks/${project.organizationSlug}/${movedTask.key}`, {
                status: newStatus,
                position: destination.index,
                sourceColumnId: oldStatus,
                destinationColumnId: newStatus,
                ...(groupByAssignee && { assigneeId: destColumn.assigneeId === 'unassigned' ? null : destColumn.assigneeId })
            });

            // Update assignee info from response before updating project tasks
            if (patchTask.data && patchTask.data.assignee) {
                movedTask.assignee = patchTask.data.assignee;
            }

            // Now update the project's tasks array with the updated movedTask
            const updatedProjectTasks = project.tasks.map(task => {
                if (task.id === movedTask.id) {
                    return {
                        ...task,
                        status: movedTask.status,
                        position: destination.index,
                        assignee: movedTask.assignee
                    };
                }
                return task;
            });

            setProject(prevProject => ({
                ...prevProject,
                tasks: updatedProjectTasks
            }));

        } catch (error) {
            console.error('Error moving task:', error);
            toast.error('Error moving task');

            // Revert the UI change if the backend update fails
            const revertColumns = [...columns];
            const revertSourceColumn = revertColumns.find(col => col.id === source.droppableId);
            const revertDestColumn = revertColumns.find(col => col.id === destination.droppableId);
            const [revertTask] = revertDestColumn.tasks.splice(destination.index, 1);
            revertSourceColumn.tasks.splice(source.index, 0, revertTask);

            // Restore original positions
            revertSourceColumn.tasks.forEach((task, index) => {
                task.position = index;
            });
            revertDestColumn.tasks.forEach((task, index) => {
                task.position = index;
            });

            setProject(prevProject => ({
                ...prevProject,
                tasks: prevProject.tasks.map(task =>
                    task.id === revertTask.id ? {
                        ...task,
                        ...revertTask,
                        status: revertTask.status,
                        position: revertTask.position,
                        assignee: revertTask.assignee
                    } : task
                )
            }));
        }
    };

    // Add min width for columns and define gap size
    const MIN_COLUMN_WIDTH = "300px";
    const COLUMN_GAP = "1rem"; // 16px, matches the gap-4 Tailwind class
    
    // Refs and state for scroll shadows
    const scrollContainerRef = useRef(null);
    const [showRightShadow, setShowRightShadow] = useState(false);
    const [showLeftShadow, setShowLeftShadow] = useState(false);
    
    // Check if scrolling is needed and update shadow visibility
    const updateScrollShadow = () => {
        if (scrollContainerRef.current) {
            const { scrollWidth, clientWidth, scrollLeft } = scrollContainerRef.current;
            
            // Show right shadow if there's more content to scroll to on the right
            setShowRightShadow(scrollWidth > clientWidth && scrollLeft + clientWidth < scrollWidth);
            
            // Show left shadow if scrolled away from the left edge
            setShowLeftShadow(scrollLeft > 0);
        }
    };
    
    // Set up scroll event listener
    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer) {
            scrollContainer.addEventListener('scroll', updateScrollShadow);
            // Initial check
            updateScrollShadow();
            
            // Check again after a short delay to ensure content is fully rendered
            const timer = setTimeout(updateScrollShadow, 100);
            
            return () => {
                scrollContainer.removeEventListener('scroll', updateScrollShadow);
                clearTimeout(timer);
            };
        }
    }, [columns, groupByAssignee, showBacklogColumn]);

    // Also check on window resize
    useEffect(() => {
        window.addEventListener('resize', updateScrollShadow);
        return () => {
            window.removeEventListener('resize', updateScrollShadow);
        };
    }, []);

    return (
        <DragDropContext onDragEnd={onDragEnd}>
            <div className="h-full relative">
                {/* Custom scrollable container */}
                <div 
                    ref={scrollContainerRef}
                    className="h-full w-full overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent"
                >
                    {groupByAssignee ? (
                        // Group by assignee layout
                        <div className="h-full">
                            {/* Status headers row - filter out backlog if not shown */}
                            <div className="flex gap-4 mb-6">
                                {project.statuses
                                    .filter(status => showBacklogColumn || status.id !== 'backlog')
                                    .map(status => (
                                        <Card key={status.id} className="bg-muted/70 border-muted-foreground/20 dark:bg-muted/20 flex-1 max-w-sm" style={{ minWidth: MIN_COLUMN_WIDTH }}>
                                            <CardHeader className="py-2.5 px-4">
                                                <CardTitle className="text-sm font-medium flex items-center gap-2">
                                                    {(() => {
                                                        const IconComponent = statusConfig[status.id]?.icon;
                                                        return IconComponent ? (
                                                            <IconComponent className={`w-4 h-4 ${STATUS_COLORS[status.color]?.text || `text-${status.color}`}`} />
                                                        ) : null;
                                                    })()}
                                                    {status.name}
                                                </CardTitle>
                                            </CardHeader>
                                        </Card>
                                    ))}
                            </div>

                            {/* Assignee groups */}
                            {Object.values(
                                columns.reduce((acc, column) => {
                                    if (!acc[column.assigneeId]) {
                                        acc[column.assigneeId] = {
                                            assigneeName: column.assigneeName,
                                            columns: []
                                        };
                                    }
                                    acc[column.assigneeId].columns.push(column);
                                    return acc;
                                }, {})
                            ).map(group => (
                                <div key={group.assigneeName} className="mb-6">
                                    <h3 className="text-lg font-semibold mb-4">{group.assigneeName}</h3>
                                    <div className="flex gap-4">
                                        {group.columns.map(column => (
                                            <div key={column.id} className="h-full flex-1 max-w-sm" style={{ minWidth: MIN_COLUMN_WIDTH }}>
                                                <Droppable droppableId={column.id}>
                                                    {(provided, snapshot) => (
                                                        <Card className={`flex h-full flex-col ${
                                                            snapshot.isDraggingOver
                                                                ? 'bg-muted/80 border-muted-foreground/30 dark:bg-muted/40'
                                                                : 'bg-muted/70 border-muted-foreground/20 dark:bg-muted/20'
                                                        }`}>
                                                            <CardContent className="flex-1 p-3">
                                                                <div
                                                                    {...provided.droppableProps}
                                                                    ref={provided.innerRef}
                                                                    className="h-full space-y-2 group"
                                                                >
                                                                    {column.tasks.map((task, index) => (
                                                                        <Draggable
                                                                            key={task.id}
                                                                            draggableId={task.id}
                                                                            index={index}
                                                                        >
                                                                            {(provided) => (
                                                                                <div
                                                                                    ref={provided.innerRef}
                                                                                    {...provided.draggableProps}
                                                                                    {...provided.dragHandleProps}
                                                                                    className="py-1"
                                                                                    data-debug-position={task.position}
                                                                                >
                                                                                    <TaskCard
                                                                                        task={task}
                                                                                        organizationSlug={project.organizationSlug}
                                                                                        onTaskUpdate={handleTaskUpdate}
                                                                                    />
                                                                                </div>
                                                                            )}
                                                                        </Draggable>
                                                                    ))}
                                                                    {provided.placeholder}
                                                                    <QuickAddTask
                                                                        status={column.originalId}
                                                                        projectId={project.id}
                                                                        organizationSlug={project.organizationSlug}
                                                                        assigneeId={column.assigneeId}
                                                                        onTaskCreated={(newTask) => {
                                                                            setProject(prevProject => ({
                                                                                ...prevProject,
                                                                                tasks: [...prevProject.tasks, newTask]
                                                                            }));
                                                                        }}
                                                                    />
                                                                </div>
                                                            </CardContent>
                                                        </Card>
                                                    )}
                                                </Droppable>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        // Original layout - now using flex instead of grid
                        <div className="flex gap-4 h-full">
                            {columns.map(column => (
                                <div key={column.id} className="h-full flex-1 max-w-sm" style={{ minWidth: MIN_COLUMN_WIDTH }}>
                                    <Droppable droppableId={column.id}>
                                        {(provided, snapshot) => (
                                            <Card className={`flex h-full flex-col ${
                                                snapshot.isDraggingOver
                                                    ? 'bg-muted/80 border-muted-foreground/30 dark:bg-muted/40'
                                                    : 'bg-muted/70 border-muted-foreground/20 dark:bg-muted/20'
                                            }`}>
                                                <CardHeader className="pb-2">
                                                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                                                        {(() => {
                                                            const statusId = groupByAssignee ? column.originalId : column.id;
                                                            const IconComponent = statusConfig[statusId]?.icon;
                                                            return IconComponent ? (
                                                                <IconComponent className={`w-4 h-4 ${STATUS_COLORS[project.statuses.find(s => s.id === statusId)?.color]?.text || statusConfig[statusId]?.color}`} />
                                                            ) : null;
                                                        })()}
                                                        {column.title}
                                                    </CardTitle>
                                                </CardHeader>
                                                <CardContent className="flex-1 p-3">
                                                    <div
                                                        {...provided.droppableProps}
                                                        ref={provided.innerRef}
                                                        className="h-full space-y-2 group"
                                                    >
                                                        {column.tasks.map((task, index) => (
                                                            <Draggable
                                                                key={task.id}
                                                                draggableId={task.id}
                                                                index={index}
                                                            >
                                                                {(provided) => (
                                                                    <div
                                                                        ref={provided.innerRef}
                                                                        {...provided.draggableProps}
                                                                        {...provided.dragHandleProps}
                                                                        className="py-1"
                                                                        data-debug-position={task.position}
                                                                    >
                                                                        <TaskCard
                                                                            task={task}
                                                                            organizationSlug={project.organizationSlug}
                                                                            onTaskUpdate={handleTaskUpdate}
                                                                        />
                                                                    </div>
                                                                )}
                                                            </Draggable>
                                                        ))}
                                                        {provided.placeholder}
                                                        <QuickAddTask
                                                            status={column.id}
                                                            projectId={project.id}
                                                            organizationSlug={project.organizationSlug}
                                                            assigneeId={null}
                                                            onTaskCreated={(newTask) => {
                                                                setProject(prevProject => ({
                                                                    ...prevProject,
                                                                    tasks: [...prevProject.tasks, newTask]
                                                                }));
                                                            }}
                                                        />
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        )}
                                    </Droppable>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                
                {/* Conditional fade effects */}
                {showRightShadow && (
                    <div className="absolute top-0 right-0 bottom-0 w-12 bg-gradient-to-l from-background to-transparent pointer-events-none" />
                )}
                
                {showLeftShadow && (
                    <div className="absolute top-0 left-0 bottom-0 w-12 bg-gradient-to-r from-background to-transparent pointer-events-none" />
                )}
            </div>
        </DragDropContext>
    );
}